import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { api } from '../api/client'
import {
  Card,
  EmptyState,
  ErrorBanner,
  Spinner,
  StatCard,
  StatusBadge,
} from '../components/common'
import { TrafficArea, TrafficBars, formatBytes } from '../components/charts'
import type { FiltriComposizione, ServiceHop } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { BottoniEsporta } from '../components/BottoniEsporta'
import Composizione from '../components/Composizione'
import { CRITICALITY_LABEL, descrizioneServizio, ServiceForm } from './Services'
import { t } from '../i18n/lingua'
import { EditorTag } from '../components/Tag'
import { DipendenzeServizio } from '../components/ServiziDiBusiness'
import { SoftwareServizio } from '../components/Sbom'
import { AppDelServizio } from '../components/BusinessApps'
import { usePermessiModuli } from '../auth/permessi'

const WINDOWS = [
  { hours: 1, label: () => t('Ultima ora') },
  { hours: 24, label: () => t('24 ore') },
  { hours: 168, label: () => t('7 giorni') },
]

/**
 * La scheda di un servizio: com'è composto e quanto traffico lo attraversa.
 *
 * La mappa è per **livelli di distanza dall'ingresso**, non un grafo disposto
 * liberamente: la domanda a cui serve rispondere è «cosa c'è dietro il punto in
 * cui entrano i clienti», e la distanza è esattamente quella risposta. Un grafo
 * a nodi sparsi la nasconderebbe dietro la disposizione.
 */
export default function ServiceDetail() {
  const { id = '' } = useParams()
  const permessi = usePermessiModuli()
  const navigate = useNavigate()
  const [hours, setHours] = useState(24)
  const [editing, setEditing] = useState(false)

  const [comuni, setComuni] = useState(false)
  const [infra, setInfra] = useState(true)
  // Le esclusioni si salvano sul servizio; qui la copia che la pagina usa
  // subito, così la composizione si ripulisce senza aspettare il salvataggio.
  const [filtri, setFiltri] = useState<FiltriComposizione | null>(null)

  const service = useAsync(() => api.service(id), [id])
  const comp = useAsync(
    () => api.serviceComposition(id, { windowHours: hours, common: comuni, infra }),
    [id, hours, comuni, infra, filtri],
  )

  // Il CI **risolto**, non quello dichiarato: un ingresso indicato per
  // indirizzo ha `ci_id` nullo, e filtrare su quello mostrerebbe i totali
  // dell'intero tenant sotto l'etichetta «traffico di questo servizio».
  const entryCiIds = (service.data?.entries ?? [])
    .map((e) => e.resolved_ci_id ?? e.ci_id)
    .filter(Boolean) as string[]
  const series = useAsync(
    () =>
      entryCiIds[0]
        ? api.flowSeries({
            window: hours <= 1 ? '1h' : hours <= 24 ? '24h' : '7d',
            ci_id: entryCiIds[0],
          })
        : Promise.resolve(null),
    [id, hours, entryCiIds[0]],
  )

  if (service.loading && !service.data) return <Spinner />
  if (service.error) return <ErrorBanner error={service.error} />
  if (!service.data) return null

  const s = service.data
  const componenti = (comp.data?.nodes ?? []).filter((n) => n.kind === 'component').length

  // I salti a mano si salvano sul servizio: la lista intera, come gli ingressi.
  const salva = async (salti: ServiceHop[]) => {
    await api.updateService(s.id, {
      hops: salti.map((h) => ({
        from_ci_id: h.from_ci_id,
        from_address: h.from_address,
        to_ci_id: h.to_ci_id,
        to_address: h.to_address,
        port: h.port,
        note: h.note,
      })),
    })
    service.reload()
    comp.reload()
  }

  const salvaFiltri = async (nuovi: FiltriComposizione) => {
    await api.updateService(s.id, { composition_filters: nuovi })
    // Il cambio di `filtri` ricalcola la composizione, dopo il salvataggio.
    setFiltri(nuovi)
    service.reload()
  }

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{s.name}</h1>
          <p className="page__subtitle">
            <StatusBadge status={s.criticality} />{' '}
            {s.owner && <>· {s.owner}</>} {s.description && <>· {descrizioneServizio(s.description)}</>}
          </p>
          <EditorTag tipo="business_service" id={s.id} />
        </div>
        <Link to="/services">{t('← Torna ai servizi')}</Link>
      </header>

      <ErrorBanner error={comp.error} />

      <div className="stats">
        <StatCard label={t('Componenti')} value={componenti} hint={t('entro {n} salti', { n: s.max_depth })} />
        <StatCard
          label={t('Punti di ingresso')}
          value={s.entries.length}
          hint={s.entries.map((e) => e.ci_name ?? e.address).join(', ') || undefined}
        />
        <StatCard
          label={t("Traffico all'ingresso")}
          value={series.data ? formatBytes(series.data.bytes_total) : '—'}
          hint={t('ultime {n} ore', { n: hours })}
        />
        <StatCard label={t('Criticità')} value={CRITICALITY_LABEL[s.criticality] ?? s.criticality}
          hint={s.declared_criticality && s.declared_criticality !== s.criticality
            ? t('dichiarata {a}, ereditata dalle Business App', { a: CRITICALITY_LABEL[s.declared_criticality] ?? s.declared_criticality })
            : undefined} />
      </div>

      <div className="toolbar" style={{ marginBottom: 16 }}>
        {WINDOWS.map((w) => (
          <button
            key={w.hours}
            className={hours === w.hours ? '' : 'secondary'}
            onClick={() => setHours(w.hours)}
          >
            {w.label()}
          </button>
        ))}
        <span style={{ flex: 1 }} />
        <BottoniEsporta
          path={(fmt) => `/services/${s.id}/export?fmt=${fmt}&window_hours=${hours}`}
          nome={`censo-servizio-${s.name}`}
        />
        <button className="secondary" onClick={() => setEditing((e) => !e)}>
          {editing ? t('Annulla') : t('Modifica')}
        </button>
        <button
          className="danger"
          onClick={async () => {
            if (!window.confirm(t('Eliminare il servizio «{nome}»?', { nome: s.name }))) return
            await api.deleteService(s.id)
            navigate('/services')
          }}
        >
          {t('Elimina')}
        </button>
      </div>

      {editing && (
        <ServiceForm
          serviceId={s.id}
          initial={{
            name: s.name,
            description: s.description,
            criticality: s.declared_criticality ?? s.criticality,
            owner: s.owner,
            max_depth: s.max_depth,
            entries: s.entries,
          }}
          onSaved={() => {
            setEditing(false)
            service.reload()
            comp.reload()
          }}
        />
      )}

      <AppDelServizio servizio={s} onChange={() => service.reload()} />

      <Card title={t("Traffico verso l'ingresso del servizio")}>
        {series.loading && !series.data ? (
          <Spinner />
        ) : (
          <TrafficArea
            points={series.data?.points ?? []}
            stepSeconds={series.data?.step_seconds ?? 300}
          />
        )}
      </Card>

      <Card title={t('Composizione del servizio')}>
        {comp.loading && !comp.data ? (
          <Spinner />
        ) : comp.data ? (
          <Composizione
            dati={comp.data}
            comuni={comuni}
            onComuni={setComuni}
            infra={infra}
            onInfra={setInfra}
            salti={s.hops ?? []}
            onAggiungiSalto={(nuovo) => salva([...(s.hops ?? []), nuovo])}
            onTogliSalto={(indice) => salva((s.hops ?? []).filter((_, i) => i !== indice))}
            filtri={filtri ?? s.composition_filters ?? { nodes: [], edges: [], ports: [] }}
            onFiltri={salvaFiltri}
          />
        ) : (
          <EmptyState title={t('Nessun componente')} hint={t('Nessun traffico osservato dagli ingressi nella finestra scelta.')} />
        )}
      </Card>

      <DipendenzeServizio serviceId={id} />

      {permessi.vede('software_map') && <SoftwareServizio serviceId={id} serviceName={s.name} />}

      <Card title={t("Ripartizione del traffico all'ingresso")}>
        <ServiceBreakdown ciId={entryCiIds[0]} hours={hours} />
      </Card>
    </>
  )
}

function ServiceBreakdown({ ciId, hours }: { ciId: string | undefined; hours: number }) {
  const window = hours <= 1 ? '1h' : hours <= 24 ? '24h' : '7d'
  const breakdown = useAsync(
    // Senza un CI di ingresso non si filtra su nulla: mostrare i totali del
    // tenant sotto questo titolo sarebbe un numero plausibile e sbagliato.
    () => (ciId ? api.flowBreakdown({ by: 'service', window, ci_id: ciId }) : Promise.resolve([])),
    [ciId, window],
  )
  if (breakdown.loading && !breakdown.data) return <Spinner />
  if (!ciId) {
    return (
      <p className="text-muted">
        {t("Nessun ingresso corrisponde a un CI noto: non c'è un punto da cui misurare.")}
      </p>
    )
  }
  return (
    <TrafficBars
      data={(breakdown.data ?? []).map((b) => ({
        key: b.key ?? b.label,
        label: b.label,
        bytes: b.bytes,
      }))}
    />
  )
}
